/**
 * Os tipos de máquina e o código que o sistema cria (0131, decisão 57): as
 * regras da tela, puras para terem teste.
 *
 * Pedido do dono (29/10/2026): o tipo sai da lista da planilha de importação,
 * e o código é do sistema: "se é a sétima escavadeira, o código já vem fechado
 * como ESC-007". A tela não digita código nenhum; ela mostra a PRÉVIA que a
 * API calcula (`/equipamentos/proximo-codigo`), e o número só é da máquina
 * quando ela grava.
 */

/** Um tipo da lista da empresa, como `GET /equipamentos/tipos` o devolve. */
export interface TipoDeEquipamento {
  readonly id: string;
  /** Gravado em caixa alta, como todo dado: "ESCAVADEIRA HIDRÁULICA". */
  readonly nome: string;
  /** O começo do código: ESC em ESC-007. */
  readonly prefixo: string;
  readonly ordem: number;
}

/** Uma máquina que o dono pediu para apontar (`GET /equipamentos/a-revisar`). */
export interface MaquinaARevisar {
  readonly id: string;
  readonly codigo: string;
  readonly tipo: string | null;
  readonly tipoDaLista: string | null;
  readonly prefixoDoTipo: string | null;
  readonly prefixoDoCodigo: string | null;
  readonly tipoDoPrefixo: string | null;
  readonly motivo: 'tipo_fora_da_lista' | 'codigo_de_outro_tipo';
}

/**
 * O nome para comparar: o mesmo de `org.nome_comparavel` no banco (maiúsculo,
 * sem acento, a barra e os espaços repetidos viram um espaço). "Guindaste/Munck"
 * e "GUINDASTE / MUNCK" são o mesmo tipo.
 */
export function nomeComparavel(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase()
    .trim()
    .replace(/\s*\/\s*|\s+/g, ' ');
}

/** O tipo da lista com este nome, em qualquer grafia, ou nulo. */
export function tipoDaLista(tipos: readonly TipoDeEquipamento[], nome: string | null | undefined): TipoDeEquipamento | null {
  if (!nome || nome.trim() === '') return null;
  const chave = nomeComparavel(nome);
  return tipos.find((t) => nomeComparavel(t.nome) === chave) ?? null;
}

/**
 * As opções da escolha do tipo, na ordem da planilha. O tipo que a máquina já
 * tem e está FORA da lista entra também, escrito "fora da lista": sem ele a
 * escolha abriria em branco e gravar sem mexer apagaria o tipo da máquina.
 */
export function opcoesDeTipo(
  tipos: readonly TipoDeEquipamento[],
  atual: string | null = null,
): readonly { readonly valor: string; readonly rotulo: string }[] {
  const daLista = [...tipos].sort((a, b) => a.ordem - b.ordem).map((t) => ({ valor: t.nome, rotulo: t.nome }));
  if (atual && atual.trim() !== '' && tipoDaLista(tipos, atual) === null) {
    return [{ valor: atual, rotulo: `${atual} · fora da lista` }, ...daLista];
  }
  return daLista;
}

/**
 * O tipo que o cadastro escolhe sozinho ao escolher a configuração: o do
 * MODELO, quando ele está na lista (escrito como a lista escreve). Fora da
 * lista, nada: quem cadastra escolhe.
 */
export function tipoSugeridoDoModelo(tipos: readonly TipoDeEquipamento[], tipoDoModelo: string | null | undefined): string {
  return tipoDaLista(tipos, tipoDoModelo)?.nome ?? '';
}

/**
 * A opção vazia da escolha do tipo na ficha: volta a herdar o tipo do MODELO
 * (0124), e diz qual é, para a pessoa saber o que escolhe.
 */
export function rotuloDoTipoDoModelo(tipoDoModelo: string | null | undefined): string {
  return tipoDoModelo && tipoDoModelo.trim() !== '' ? `Igual ao modelo: ${tipoDoModelo}` : 'Igual ao modelo';
}

/** A frase do código no cadastro: a prévia, ou o que falta para haver uma. */
export function textoDoCodigoNovo(tipo: string, previa: string | null | undefined): string {
  if (tipo.trim() === '') return 'Escolha o tipo: o código nasce dele.';
  if (!previa) return 'O código nasce do tipo ao salvar.';
  return `${previa} · criado ao salvar`;
}

/** A ajuda do código na ficha, que ninguém edita. */
export const AJUDA_DO_CODIGO = 'Criado pelo sistema pelo tipo e pela sequência.';

/**
 * A ajuda do código na ficha de uma máquina QUE JÁ EXISTE (decisão 64): o
 * adesivo impresso leva o código no texto e no QR. O ajuste da ficha não muda
 * o código (0131), mas se ele mudar por outro caminho (correção, importação),
 * o adesivo colado continua com o antigo, e só um adesivo novo conserta.
 */
export const AJUDA_DO_CODIGO_NA_FICHA = `${AJUDA_DO_CODIGO} É o que vai no adesivo e no QR: se ele mudar, o adesivo colado fica com o antigo.`;

/** O que está errado numa máquina a revisar, na frase que a tela escreve. */
export function motivoDaRevisao(m: MaquinaARevisar): string {
  if (m.motivo === 'tipo_fora_da_lista') {
    return m.tipo ? `O tipo "${m.tipo}" está fora da lista` : 'Sem tipo';
  }
  const tipo = m.tipoDaLista ?? m.tipo ?? 'sem tipo';
  if (m.tipoDoPrefixo) return `O código ${m.codigo} é de ${m.tipoDoPrefixo}, o tipo é ${tipo}`;
  return `O prefixo ${m.prefixoDoCodigo ?? ''} do código ${m.codigo} não é de nenhum tipo; ${tipo} usa ${m.prefixoDoTipo ?? 'outro'}`;
}

/** O que fazer com ela: escolher o tipo certo na ficha. */
export function comoResolverARevisao(m: MaquinaARevisar): string {
  if (m.motivo === 'tipo_fora_da_lista') return 'Escolha o tipo na lista, na ficha da máquina.';
  return m.tipoDoPrefixo
    ? `Se a máquina é mesmo ${m.tipoDoPrefixo}, troque o tipo na ficha. O código não muda.`
    : 'Confira o tipo na ficha. O código não muda.';
}

/** "1 máquina para revisar", "12 máquinas para revisar". */
export function textoDaRevisao(quantas: number): string {
  return `${String(quantas)} ${quantas === 1 ? 'máquina' : 'máquinas'} para revisar o tipo`;
}
